/**
 * Helpers for keeping work-panel membership role rows consistent between the
 * invitation store and locally staged search invitees.
 */

export function normalizeMembershipProfileId(profileId) {
    if (profileId == null) {
        return null;
    }

    const numericId = Number(profileId);

    return Number.isNaN(numericId) ? profileId : numericId;
}

export function normalizeMembershipEmail(email) {
    return email?.trim?.().toLowerCase() ?? "";
}

/**
 * @param {object} invitationStore
 * @param {number|string|null|undefined} profileId
 * @param {string|null|undefined} [email]
 */
export function findInvitationRoleProfile(
    invitationStore,
    profileId,
    email = null,
) {
    const normalizedId = normalizeMembershipProfileId(profileId);
    const profiles = invitationStore?.user_profiles_with_roles ?? [];

    if (normalizedId != null) {
        const byId = profiles.find(
            (profile) =>
                normalizeMembershipProfileId(profile?.id) === normalizedId,
        );

        if (byId) {
            return byId;
        }
    }

    const normalizedEmail = normalizeMembershipEmail(email);

    if (!normalizedEmail) {
        return null;
    }

    return (
        profiles.find(
            (profile) =>
                profile?.non_registered &&
                normalizeMembershipEmail(profile?.email) === normalizedEmail,
        ) ?? null
    );
}

export function resolveInitialRoleForMember(member, defaultRole) {
    const roleProfile = member?.role;

    return typeof roleProfile === "string" && roleProfile.length > 0
        ? roleProfile
        : defaultRole;
}

export function resolveInitialParticipationForMember(
    member,
    defaultParticipation = true,
) {
    if (typeof member?.is_conversation_participant === "boolean") {
        return member.is_conversation_participant;
    }

    return defaultParticipation;
}

/**
 * @param {object} member
 * @param {string} accessLevel
 * @param {object} invitationStore
 */
export function buildWorkPanelRoleProfileFromMember(
    member,
    accessLevel,
    invitationStore,
) {
    const fakeProfile = {
        id: member?.id,
        role: resolveInitialRoleForMember(
            member,
            invitationStore.ROLES.MEMBER,
        ),
        access_level: accessLevel,
        is_conversation_participant: resolveInitialParticipationForMember(
            member,
            true,
        ),
        has_parent_access: member?.has_parent_access ?? false,
    };

    if (member?.non_registered) {
        fakeProfile.email = member.email;
        fakeProfile.non_registered = true;
    }

    return fakeProfile;
}

/**
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {object} params.member
 * @param {string} params.accessLevel
 */
export function ensureWorkPanelMemberRoleProfile({
    invitationStore,
    member,
    accessLevel,
}) {
    if (!member?.id || member?.is_agent) {
        return null;
    }

    const existing = findInvitationRoleProfile(
        invitationStore,
        member.id,
        member.email,
    );

    if (existing) {
        if (member.non_registered) {
            if (member.email && !existing.email) {
                existing.email = member.email;
            }

            existing.non_registered = true;
        }

        return existing;
    }

    const roleProfile = buildWorkPanelRoleProfileFromMember(
        member,
        accessLevel,
        invitationStore,
    );

    invitationStore.user_profiles_with_roles.push(roleProfile);

    return roleProfile;
}

/**
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {number|string} params.profileId
 * @param {string} params.role
 * @param {boolean} [params.preserveChatAccess]
 */
export function applyWorkPanelRoleChange({
    invitationStore,
    profileId,
    role,
    preserveChatAccess = true,
}) {
    invitationStore.updateRole(role, profileId);

    if (!preserveChatAccess) {
        invitationStore.updateParticipantAccess(
            profileId,
            role !== invitationStore.ROLES.OBSERVER,
        );

        return;
    }

    if (role === invitationStore.ROLES.OBSERVER) {
        invitationStore.updateParticipantAccess(profileId, false);
    }
}

/**
 * Merge invitation-store role/participation onto a selectable profile object.
 *
 * @param {object} invitationStore
 * @param {object|null|undefined} profile
 */
export function enrichSelectableProfileWithInvitationRole(
    invitationStore,
    profile,
) {
    if (!profile?.id) {
        return profile;
    }

    const roleProfile = findInvitationRoleProfile(
        invitationStore,
        profile.id,
        profile.email,
    );

    if (!roleProfile) {
        return profile;
    }

    return {
        ...profile,
        role: roleProfile.role,
        is_conversation_participant: roleProfile.is_conversation_participant,
    };
}
